import * as PropTypes from 'prop-types';
import {
    ClientMode,
    EditorLayersMode,
    EditorTool,
    EditorToolType,
    FeatureZoom,
    FloatPosition,
    KeyboardShortcut,
    LineStyle,
    MarkerAnchor,
    StyleElement,
} from '../enums';
import {
    Color,
    EditorToolDefinition,
    ExtendedEditorToolDefinition,
    FeatureDeleteRequest,
    FeatureZoomOptions,
    OptValidator,
    PixelPadding,
    StyleDefinition,
} from '../types';
import {Feature} from 'geojson';
import {Requireable} from 'react';

/**
 * Properties specifické pro mapový editor.
 */
export interface EditorProps {
    /**
     * Příznak, zda je editor v read-only režimu.
     * V tomto režimu jsou dostupné pouze nástroje neumožňující modifikaci features.
     * Výchozí hodnota: `false`
     */
    readOnly?: boolean;

    /**
     * Režim editačních vrstev.
     * Výchozí hodnota: {@link EditorLayersMode.OVERLAY}.
     */
    layersMode?: EditorLayersMode;

    /**
     * Definice stylů pro vykreslení editovaných features.
     *
     * Pouze pro režim editačních vrstev {@link EditorLayersMode.OVERLAY}.
     * Feature se na daný styl musí odkazovat pomocí své property `styleId`
     * nebo `selectStyleId`. V opačném případě bude pro vykreslení feature
     * použita základní barva {@link color}, či případně styl nástroje určený
     * dle feature property `toolId`.
     */
    styles?: StyleDefinition[];

    /**
     * Základní barva editovaných features.
     * Pouze pro režim editačních vrstev {@link EditorLayersMode.OVERLAY}.
     * Výchozí hodnota: Barva definovaná na straně Mapio.
     */
    color?: Color;

    /**
     * Schéma barevného provedení editoru.
     */
    colorScheme?: 'dark' | 'light' | 'system';

    /**
     * Šířka čáry editovaných features.
     * Pouze pro režim editačních vrstev {@link EditorLayersMode.OVERLAY}.
     * Výchozí hodnota: Šířka definovaná na straně Mapio.
     */
    lineWidth?: number;

    /**
     * Nástroje dostupné v toolbaru nebo jako plovoucí tlačítka nad mapou.
     */
    tools?: (EditorTool | EditorToolDefinition | ExtendedEditorToolDefinition)[];

    /**
     * Nástroje dostupné skrze kontextové menu myši.
     */
    contextTools?: (EditorTool | EditorToolDefinition)[];

    /**
     * Povolené klávesové zkratky.
     * Výchozí hodnota: `[KeyboardShortcut.DELETE]`.
     */
    keyboardShortcuts?: KeyboardShortcut[];

    /**
     * Callback volaný při požadavku na smazání features z editoru uživatelem.
     *
     * Pokud je callback definován, nebude zobrazeno stanradní modální potvrzovací
     * okno, ale dojde místo toho k vyvolání předaného callbacku. Programátor má
     * má tak možnost zobrazit vlastní imlementaci potvrzení uživatele a následně
     * pomocí akce {@link EditorActions.deleteFeatures} dané features skutečně
     * smazat.
     *
     * @param request - Požadavek na smazání features obsahující jejich identifikaci.
     */
    featureDeleteHandler?: (request: FeatureDeleteRequest) => void;

    /**
     * Příznak, zda zobrazovat editované features při tisku.
     * Výchozí hodnota: `false`.
     */
    featurePrintEnabled?: boolean;

    /**
     * Výchozí features jež se mají načíst pro editaci.
     * Pouze pro režim editačních vrstev {@link EditorLayersMode.OVERLAY}.
     */
    initialFeatures?: Feature[];

    /**
     * Parametry pro načtení {@link initialFeatures}.
     */
    initialFeaturesOptions?: FeatureZoomOptions;

    /**
     * Režim zobrazení, určující URL pro zobrazení v mapové komponentě.
     * Výchozí hodnota: {@link ClientMode.EMBED_EDITOR}
     */
    clientMode?: ClientMode;

    /**
     * Množina vzorů ID vrstev, omezující které vrstvy se mají nabízet pro editaci v editačním režimu.
     * Pokud vzor končí '*', považuje se za prefix. V opačném případě se porovnává na přesnou shodu.
     * Pokud je null nebo prázdné pole, zobrazují se všechny vrstvy.
     * Má význam pouze pro režim {@link EditorLayersMode.DATA}.
     */
    editableLayerIds?: string[];
}

/**
 * Typy properties položek definice nástroje editoru (bez `items`).
 *
 * @ignore
 */
const editorToolDefinitionMemberPropTypes = {
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(Object.values(EditorToolType)).isRequired,
    extend: PropTypes.bool as OptValidator<boolean>,
    position: PropTypes.oneOf(Object.values(FloatPosition)) as OptValidator<FloatPosition>,
    icon: PropTypes.string as OptValidator<string>,
    marker: PropTypes.string as OptValidator<string>,
    name: PropTypes.string as OptValidator<string>,
    description: PropTypes.string as OptValidator<string>,
    visible: PropTypes.bool as OptValidator<boolean>,
    enabled: PropTypes.bool as OptValidator<boolean>,
    active: PropTypes.bool as OptValidator<boolean>,
    // Záměrně tu není `items`.
};

/**
 * Typy properties položek definice rozšířeného nástroje editoru (bez `items`).
 *
 * @ignore
 */
const extendedEditorToolDefinitionMemberPropTypes = {
    ...editorToolDefinitionMemberPropTypes,
    iconActive: PropTypes.string as OptValidator<string>,
    horizontal: PropTypes.bool as OptValidator<boolean>,
    order: PropTypes.number as OptValidator<number>,
    section: PropTypes.number as OptValidator<number>,
    rememberSecondary: PropTypes.bool as OptValidator<boolean>,
    // Záměrně tu není `items`.
};

/**
 * Typ property definice nástroje editoru.
 *
 * @ignore
 */
const editorToolDefinitionPropType = PropTypes.exact({
    ...editorToolDefinitionMemberPropTypes,
    items: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.oneOf(Object.values(EditorTool)) as Requireable<EditorTool>,
            PropTypes.exact(editorToolDefinitionMemberPropTypes),
        ]).isRequired,
    ) as OptValidator<(EditorTool | Exclude<EditorToolDefinition, 'items'>)[]>,
});

/**
 * Typ property definice rozšířeného nástroje editoru.
 *
 * @ignore
 */
const extendedEditorToolDefinitionPropType = PropTypes.exact({
    ...extendedEditorToolDefinitionMemberPropTypes,
    items: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.oneOf(Object.values(EditorTool)) as Requireable<EditorTool>,
            PropTypes.exact(extendedEditorToolDefinitionMemberPropTypes),
        ]).isRequired,
    ) as OptValidator<(EditorTool | Exclude<ExtendedEditorToolDefinition, 'items'>)[]>,
});

/**
 * Typ property nástroje editoru.
 *
 * @ignore
 */
const editorToolPropType = PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(EditorTool)) as Requireable<EditorTool>,
    editorToolDefinitionPropType,
    extendedEditorToolDefinitionPropType,
]);

/**
 * Typy properties specifických pro mapový editor.
 *
 * @ignore
 */
export const editorPropTypes = {
    readOnly: PropTypes.bool,
    layersMode: PropTypes.oneOf(Object.values(EditorLayersMode)),
    styles: PropTypes.arrayOf(
        PropTypes.exact({
            id: PropTypes.string.isRequired,
            elements: PropTypes.arrayOf(PropTypes.oneOf(Object.values(StyleElement)).isRequired) as OptValidator<
                StyleElement[]
            >,
            color: PropTypes.oneOfType([
                PropTypes.arrayOf(PropTypes.number.isRequired) as OptValidator<Color>,
                PropTypes.string,
            ]) as OptValidator<Color | string>,
            lineStyle: PropTypes.oneOf(Object.values(LineStyle)) as OptValidator<LineStyle>,
            lineWidth: PropTypes.number as OptValidator<number>,
            lineColor: PropTypes.oneOfType([
                PropTypes.arrayOf(PropTypes.number.isRequired) as OptValidator<Color>,
                PropTypes.string,
            ]) as OptValidator<Color | string>,
            arrowLineColor: PropTypes.oneOfType([
                PropTypes.arrayOf(PropTypes.number.isRequired) as OptValidator<Color>,
                PropTypes.string,
            ]) as OptValidator<Color | string>,
            fillColor: PropTypes.oneOfType([
                PropTypes.arrayOf(PropTypes.number.isRequired) as OptValidator<Color>,
                PropTypes.string,
            ]) as OptValidator<Color | string>,
            marker: PropTypes.string as OptValidator<string>,
            markerColor: PropTypes.oneOfType([
                PropTypes.arrayOf(PropTypes.number.isRequired) as OptValidator<Color>,
                PropTypes.string,
            ]) as OptValidator<Color | string>,
            markerAnchor: PropTypes.oneOf(Object.values(MarkerAnchor)) as OptValidator<MarkerAnchor>,
            markerAutoScale: PropTypes.bool as OptValidator<boolean>,
            markerOffsetX: PropTypes.number as OptValidator<number>,
            markerOffsetY: PropTypes.number as OptValidator<number>,
            labelSourceProperty: PropTypes.string as OptValidator<string>,
            labelFontFamily: PropTypes.string as OptValidator<string>,
            labelFontSize: PropTypes.string as OptValidator<string>,
            labelFontBold: PropTypes.bool as OptValidator<boolean>,
            labelOffsetX: PropTypes.number as OptValidator<number>,
            labelOffsetY: PropTypes.number as OptValidator<number>,
        }).isRequired,
    ),
    color: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.number) as OptValidator<Color>,
        PropTypes.string,
    ]) as OptValidator<Color | string>,
    colorScheme: PropTypes.oneOf(['dark', 'light', 'system']) as OptValidator<'dark' | 'light' | 'system'>,
    lineWidth: PropTypes.number,
    tools: PropTypes.arrayOf(editorToolPropType.isRequired),
    contextTools: PropTypes.arrayOf(editorToolPropType.isRequired),
    keyboardShortcuts: PropTypes.oneOf(Object.values(KeyboardShortcut)),
    featureDeleteHandler: PropTypes.func,
    featurePrintEnabled: PropTypes.bool,
    initialFeatures: PropTypes.array,
    initialFeaturesOptions: PropTypes.exact({
        zoom: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(Object.values(FeatureZoom))]) as OptValidator<
            boolean | FeatureZoom
        >,
        zoomPadding: PropTypes.arrayOf(PropTypes.number) as OptValidator<PixelPadding>,
        zoomRatio: PropTypes.number as OptValidator<number>,
        zoomMinScale: PropTypes.number as OptValidator<number>,
    }),
    editableLayerIds: PropTypes.array,
};

/**
 * Výchozí hodnoty properties specifických pro mapový editor.
 *
 * @ignore
 */
export const editorDefaultProps = {
    // Zcela záměrně neuvádíme default hodnoty properties, které se synchronizují s mapio.
    // 1) Jsou definovány na straně mapio. Neduplikujeme tím pádem logiku
    //    a neposíláme zbytečné zprávy přes frame.
    // 2) V rámci zpětné kompatibility udržujeme některé deprecated properties.
    //    Default ostatních properties by je mohl přepsat (pokud by šlo o alias).
    debugName: 'MapEditor',
};
